import { CONSTANTS } from "../../utils/Constants";
import { CACHE_KEYS } from "../local/CacheManager";
import { Method, URLs } from "./Configuration";
import { EventSource } from "eventsource";

interface IMainService {
  url: string;
  method?: Method;
  params?: {};
  headers?: {};
  isAuthenticated?: boolean;
}

interface IApiResponse<T> {
  status: number;
  ok: boolean;
  data: T;
}

interface IRealtimeConfig<T> extends IMainService {
  onMessage: (data: T) => void;
  onError?: (error: any) => void;
  onOpen?: () => void;
}

/**
 * Generic realtime API function using Server-Sent Events (SSE)
 */
export const fetchAPIRealtime = <T>({
  url,
  headers,
  isAuthenticated = false,
  onMessage,
  onError,
  onOpen,
}: IRealtimeConfig<T>) => {
  const generatedUrl = generateURL(url);

  const token = localStorage.getItem(CACHE_KEYS.TOKEN);

  /**
   * Native EventSource does not support custom headers.
   * So we pass token in query params if needed.
   */

  const eventSource = new EventSource(generatedUrl, {
    fetch: (input, init) =>
      fetch(input, {
        ...init,
        headers: {
          ...init.headers,
          Authorization: `Bearer ${token}`,
        },
      }),
  });

  eventSource.onopen = () => {
    console.log(
      CONSTANTS.STRINGS.SUCCESS,
      CONSTANTS.STRINGS.REALTIME_CONNECTION_OPENED,
    );
    onOpen?.();
  };

  eventSource.onmessage = (event) => {
    try {
      const parsedData: T = JSON.parse(event.data);
      onMessage(parsedData);
    } catch (error) {
      console.error("Failed to parse realtime data", error);
      onError?.(error);
    }
  };

  eventSource.onerror = (error) => {
    console.error("Realtime connection error", error);
    onError?.(error);

    /**
     * Optional:
     * Close connection on fatal error
     */
    // eventSource.close();
  };

  /**
   * Return cleanup function
   */
  return () => {
    eventSource.close();
  };
};

/**
 * Generates the full URL for the API endpoint.
 */
const generateURL = (route: string): string => {
  const baseUrl =
    process.env.NODE_ENV === "production"
      ? URLs.PROD_BASE_URL_API
      : URLs.DEV_BASE_URL_API;

  return `${baseUrl}${route}`;
};
